import React, { Fragment, useRef } from "react";

import { Typography, Box, Divider, CircularProgress } from "@mui/material";

import { TripCategory } from "types";
import { CreateTripState } from "pages/new/context/initialState";
import VehicleCard from "./components/VehicleCard";
import useLoadVehicles from "./hooks/useLoadVehicles";
import useVehicleFilters from "./hooks/useVehicleFilters";
import { useScreenSize } from "globals/hooks";
import {
  FilterButton,
  FilterDropdown,
  ActiveFilterChips,
} from "./components/VehicleFilters";

type VehicleListProps = {
  trip: CreateTripState;
  tripCategory: TripCategory;
  vehicleId: string;
  onVehicleSelect: (vehicle: any) => () => void;
  returnTrip?: CreateTripState;
};

function VehicleList(props: VehicleListProps) {
  const { trip, returnTrip, onVehicleSelect, tripCategory } = props;
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  // hooks
  const { isMobileView } = useScreenSize();
  const { vehicles, isLoadingVehicles, isLoadingBRA } = useLoadVehicles({
    trip,
    returnTrip,
    tripCategory,
  });

  const {
    isFilterOpen,
    filters,
    hasActiveFilters,
    activeFilterCount,
    handleFilterOpen,
    handleFilterClose,
    updateFilter,
    clearFilter,
    filterVehicles,
  } = useVehicleFilters({
    defaultMinCapacity: trip.totalGroupSize,
  });

  // Apply filters to vehicles with safety check
  const filteredVehicles = filterVehicles(vehicles || []);

  return (
    <>
      <Box mt={5} mb={2} mx={isMobileView ? 0.5 : 0}>
        {isMobileView ? (
          <>
            {/* Mobile Layout */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3">Choose Vehicle</Typography>
              <FilterButton
                ref={filterButtonRef}
                onClick={handleFilterOpen}
                filterCount={activeFilterCount}
              />
            </Box>
            {hasActiveFilters && (
              <Box mt={1}>
                <ActiveFilterChips
                  filters={filters}
                  onRemoveFilter={clearFilter}
                />
              </Box>
            )}
          </>
        ) : (
          /* Desktop Layout */
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h3" sx={{ flexShrink: 0 }}>
              Choose Vehicle
            </Typography>
            <Box
              sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
            >
              {hasActiveFilters && (
                <ActiveFilterChips
                  filters={filters}
                  onRemoveFilter={clearFilter}
                />
              )}
            </Box>
            <FilterButton
              ref={filterButtonRef}
              onClick={handleFilterOpen}
              filterCount={activeFilterCount}
            />
          </Box>
        )}
      </Box>
      {isLoadingVehicles && vehicles.length === 0 && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Box mb={2}>Loading vehicles...</Box>
            <Box mb={2}>
              <CircularProgress />
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {filteredVehicles.map((vehicle) => {
        return (
          <Fragment key={vehicle.id}>
            <VehicleCard
              isLoadingBRA={isLoadingBRA}
              onVehicleSelect={onVehicleSelect(vehicle)}
              vehicle={vehicle}
            />
            <Box mt={3}>
              <Divider />
            </Box>
          </Fragment>
        );
      })}

      <FilterDropdown
        isOpen={isFilterOpen}
        onClose={handleFilterClose}
        anchorEl={filterButtonRef.current}
        filters={filters}
        onFilterChange={updateFilter}
        passengerCount={trip.totalGroupSize}
      />
    </>
  );
}

export default VehicleList;
